/**
 * MatchRuleOnEntity.tsx
 * Dedup rule table + add/edit modal for entity-level match rules.
 * Redesigned — uses shared GenWizard light theme.
 */

import React, { Component } from 'react';
import Select from 'react-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faPlus } from '@fortawesome/free-solid-svg-icons';

import { T, cls, injectTheme, selectStyles } from '../theme/theme';

// ─── Types ────────────────────────────────────────────────────────────────────
interface MatchField {
  field:      string;
  match_type: 'EXACT' | 'FUZZY' | 'NUMERIC_RANGE' | 'LOOKUP_NORMALIZED';
  weight:     number;
}

interface DedupRule {
  rule_name:    string;
  match_fields: MatchField[];
  confidence:   number;
  merge_action: 'manual-review' | 'auto-merge';
}

interface MatchRuleOnEntityProps {
  dedupsRules:      DedupRule[];
  fieldOptionsModel: any;
}

interface MatchRuleOnEntityState {
  dedupsRules:          DedupRule[];
  showModal:            boolean;
  newRule:              DedupRule;
  groupedFieldOptions:  any[];
  editingIndex?:        number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
const MATCH_TYPE_BADGE: Record<string, string> = {
  EXACT:              'gw-badge-green',
  FUZZY:              'gw-badge-amber',
  NUMERIC_RANGE:      'gw-badge-brand',
  LOOKUP_NORMALIZED:  'gw-badge-muted',
};

const defaultRule = (): DedupRule => ({
  rule_name: '', match_fields: [], confidence: 70, merge_action: 'manual-review',
});

// ─── Component ────────────────────────────────────────────────────────────────
class MatchRuleOnEntity extends Component<MatchRuleOnEntityProps, MatchRuleOnEntityState> {
  state: MatchRuleOnEntityState = {
    dedupsRules:         [],
    showModal:           false,
    newRule:             defaultRule(),
    groupedFieldOptions: [],
  };

  componentDidMount() {
    injectTheme();
    this.setState({
      groupedFieldOptions: this.extractFieldOptions(this.props.fieldOptionsModel),
      dedupsRules:         this.props.dedupsRules || [],
    });
  }

  // ── Field option extraction ───────────────────────────────────────────────
  extractFieldOptions = (dm: any): any[] => {
    const groups: Record<string, any> = {};
    const recurse = (node: any) => {
      if (!node?.field) return;
      for (const f of node.field) {
        if (f.eClass === 'Field') {
          const g = f.TABLE_DISPLAY_NM || f.TABLE_NM || 'Other';
          if (!groups[g]) groups[g] = { label: g, options: [] };
          groups[g].options.push({
            value: f.FLD_ID,
            label: f.COLUMN_DISPLAY_NAME || f.COLUMN_NAME,
          });
        } else if (f.eClass === 'FieldGroup') {
          recurse(f);
        }
      }
    };
    recurse(dm);
    return Object.values(groups);
  };

  // ── CRUD ──────────────────────────────────────────────────────────────────
  openAddModal = () =>
    this.setState({ showModal: true, newRule: defaultRule(), editingIndex: undefined });

  editRule = (i: number) =>
    this.setState({ newRule: { ...this.state.dedupsRules[i] }, editingIndex: i, showModal: true });

  deleteRule = (i: number) => {
    const d = [...this.state.dedupsRules];
    d.splice(i, 1);
    this.setState({ dedupsRules: d });
  };

  handleAddRule = () => {
    const { dedupsRules, newRule, editingIndex } = this.state;
    const updated = editingIndex !== undefined
      ? dedupsRules.map((r, i) => (i === editingIndex ? newRule : r))
      : [...dedupsRules, newRule];
    this.setState({ dedupsRules: updated, showModal: false, editingIndex: undefined, newRule: defaultRule() });
  };

  // ── Match field mutations ─────────────────────────────────────────────────
  addMatchField = () =>
    this.setState(prev => ({
      newRule: {
        ...prev.newRule,
        match_fields: [...prev.newRule.match_fields, { field: '', match_type: 'EXACT', weight: 0 }],
      },
    }));

  removeMatchField = (i: number) => {
    const mf = [...this.state.newRule.match_fields];
    mf.splice(i, 1);
    this.setState({ newRule: { ...this.state.newRule, match_fields: mf } });
  };

  handleFieldChange = (idx: number, key: keyof MatchField, val: any) => {
    const mf = [...this.state.newRule.match_fields];
    mf[idx] = { ...mf[idx], [key]: key === 'weight' ? Number(val) : val };
    this.setState({ newRule: { ...this.state.newRule, match_fields: mf } });
  };

  // ── Label resolution ──────────────────────────────────────────────────────
  getFieldLabel = (fldVal: string): string => {
    for (const g of this.state.groupedFieldOptions) {
      const f = g.options.find((o: any) => o.value === fldVal);
      if (f) return `${g.label} · ${f.label}`;
    }
    return fldVal;
  };

  // ── Modal ─────────────────────────────────────────────────────────────────
  renderModal() {
    const { showModal, newRule, groupedFieldOptions, editingIndex } = this.state;
    if (!showModal) return null;

    const isEdit = editingIndex !== undefined;

    // Modal-scoped select styles (wider menu to accommodate group labels)
    const modalSelectStyles = {
      ...selectStyles,
      menu: (b: any) => ({ ...b, zIndex: 9999, borderRadius: 8, border: `1px solid ${T.border}`, minWidth: 320 }),
    };

    return (
      <div className="gw-modal-backdrop" onClick={e => { if (e.target === e.currentTarget) this.setState({ showModal: false }); }}>
        <div className="gw-modal">

          {/* Header */}
          <div className="gw-modal-hdr">
            <div className="gw-modal-title">{isEdit ? 'Edit Rule' : 'New Dedup Rule'}</div>
            <button className="gw-close-btn" onClick={() => this.setState({ showModal: false })}>×</button>
          </div>

          {/* Body */}
          <div className="gw-modal-body">

            {/* Rule name + confidence + merge */}
            <div className="gw-form-grid">
              <div className="gw-form-group" style={{ gridColumn: '1 / -1' }}>
                <label className="gw-label">Rule Name</label>
                <input className="gw-input" value={newRule.rule_name}
                  onChange={e => this.setState({ newRule: { ...newRule, rule_name: e.target.value } })}
                  placeholder="e.g. Name + Email Match" />
              </div>

              <div className="gw-form-group">
                <label className="gw-label">Confidence — {newRule.confidence}%</label>
                <input type="range" className="gw-slider" min={0} max={100} value={newRule.confidence}
                  style={{ width: '100%', accentColor: T.brand }}
                  onChange={e => this.setState({ newRule: { ...newRule, confidence: +e.target.value } })} />
              </div>

              <div className="gw-form-group">
                <label className="gw-label">Merge Action</label>
                <select className="gw-select" value={newRule.merge_action}
                  onChange={e => this.setState({ newRule: { ...newRule, merge_action: e.target.value as any } })}>
                  <option value="manual-review">Manual Review</option>
                  <option value="auto-merge">Auto Merge</option>
                </select>
              </div>
            </div>

            {/* Match fields section header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '16px 0 10px' }}>
              <div className="gw-section-hdr" style={{ flex: 1, marginBottom: 0 }}>Match Fields</div>
              <button className="gw-btn gw-btn-outline gw-btn-sm" onClick={this.addMatchField}>
                <FontAwesomeIcon icon={faPlus} style={{ fontSize: 10 }} /> Add Field
              </button>
            </div>

            {/* Match field rows */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {newRule.match_fields.map((mf, idx) => (
                <div key={idx} className="gw-mf-row">
                  {/* Field selector */}
                  <div className="gw-form-group">
                    <label className="gw-label">Field</label>
                    <div className="gw-select-wrap">
                      <Select
                        options={groupedFieldOptions}
                        value={groupedFieldOptions.flatMap(g => g.options).find((o: any) => o.value === mf.field) || null}
                        onChange={s => this.handleFieldChange(idx, 'field', s?.value)}
                        styles={modalSelectStyles}
                        classNamePrefix="select"
                        placeholder="Select field…"
                        isSearchable
                      />
                    </div>
                  </div>

                  {/* Match type */}
                  <div className="gw-form-group">
                    <label className="gw-label">Match Type</label>
                    <select className="gw-select" value={mf.match_type}
                      style={{ width: 160 }}
                      onChange={e => this.handleFieldChange(idx, 'match_type', e.target.value)}>
                      <option>EXACT</option>
                      <option>FUZZY</option>
                      <option>NUMERIC_RANGE</option>
                      <option>LOOKUP_NORMALIZED</option>
                    </select>
                  </div>

                  {/* Weight */}
                  <div className="gw-form-group">
                    <label className="gw-label">Weight %</label>
                    <input className="gw-input" type="number" value={mf.weight}
                      min={0} max={100} style={{ width: 72 }}
                      onChange={e => this.handleFieldChange(idx, 'weight', e.target.value)} />
                  </div>

                  {/* Delete */}
                  <button
                    className="gw-btn gw-btn-danger gw-btn-icon gw-btn-sm"
                    style={{ alignSelf: 'flex-end' }}
                    onClick={() => this.removeMatchField(idx)}
                  >
                    <FontAwesomeIcon icon={faTrash} style={{ fontSize: 11 }} />
                  </button>
                </div>
              ))}

              {newRule.match_fields.length === 0 && (
                <div style={{ fontSize: 12, color: T.textMuted, padding: '8px 0' }}>
                  No match fields yet — click <strong>Add Field</strong> to add one.
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="gw-modal-foot">
            <button className="gw-btn gw-btn-ghost gw-btn-sm"
              onClick={() => this.setState({ showModal: false })}>Cancel</button>
            <button className="gw-btn gw-btn-primary gw-btn-sm" onClick={this.handleAddRule}>
              {isEdit ? 'Update Rule' : 'Add Rule'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Rule table ────────────────────────────────────────────────────────────
  renderTable() {
    const { dedupsRules } = this.state;
    if (!dedupsRules.length) {
      return (
        <div className="gw-empty">
          <div className="gw-empty-icon">📋</div>
          <div className="gw-empty-label">No dedup rules defined. Click <strong>New Rule</strong> to add one.</div>
        </div>
      );
    }

    return (
      <div className="gw-card">
        <table className="gw-table">
          <thead>
            <tr>
              <th>Rule Name</th>
              <th>Match Fields</th>
              <th>Confidence</th>
              <th>Merge Action</th>
              <th style={{ width: 68 }}></th>
            </tr>
          </thead>
          <tbody>
            {dedupsRules.map((rule, i) => (
              <tr key={i}>
                <td style={{ fontWeight: 700 }}>{rule.rule_name}</td>

                <td>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                    {rule.match_fields.map((mf, j) => (
                      <div key={j} style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 12 }}>{this.getFieldLabel(mf.field)}</span>
                        <span className={cls('gw-badge', MATCH_TYPE_BADGE[mf.match_type] || 'gw-badge-muted')}>
                          {mf.match_type}
                        </span>
                        <span style={{ fontSize: 11, color: T.textMuted }}>{mf.weight}%</span>
                      </div>
                    ))}
                  </div>
                </td>

                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div className="gw-conf-bar" style={{ width: 60 }}>
                      <div className="gw-conf-fill" style={{ width: `${rule.confidence}%` }} />
                    </div>
                    <span style={{ fontSize: 12, fontWeight: 700, color: T.brand }}>
                      {rule.confidence}%
                    </span>
                  </div>
                </td>

                <td>
                  <span className={cls(
                    'gw-badge',
                    rule.merge_action === 'auto-merge' ? 'gw-badge-green' : 'gw-badge-amber',
                  )}>
                    {rule.merge_action === 'auto-merge' ? 'Auto Merge' : 'Manual Review'}
                  </span>
                </td>

                <td>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button className="gw-btn gw-btn-ghost gw-btn-icon gw-btn-sm"
                      title="Edit" onClick={() => this.editRule(i)}>
                      <FontAwesomeIcon icon={faEdit} style={{ fontSize: 11 }} />
                    </button>
                    <button className="gw-btn gw-btn-danger gw-btn-icon gw-btn-sm"
                      title="Delete" onClick={() => this.deleteRule(i)}>
                      <FontAwesomeIcon icon={faTrash} style={{ fontSize: 11 }} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  render() {
    return (
      <div className="gw-root">
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
          <button className="gw-btn gw-btn-primary gw-btn-sm" onClick={this.openAddModal}>
            <FontAwesomeIcon icon={faPlus} style={{ fontSize: 10 }} /> New Rule
          </button>
        </div>

        {this.renderTable()}
        {this.renderModal()}
      </div>
    );
  }
}

export default MatchRuleOnEntity;